import { Box, Flex, Grid, Progress, Text, useColorModeValue } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import ProfileAvatarUploader from "./ProfileAvatarUploader";
import PremiumVerifiedBadge from "./ui/PremiumVerifiedBadge";

const AVATAR_SIZE = "48px";

export default function SettingsProfileHeader({
  userId,
  user,
  avatarUrl,
  onAvatarUpdated,
  completionPercent = 0,
  isProfileFullyComplete = false,
}) {
  const { t } = useTranslation();
  const titleColor = useColorModeValue("gray.900", "white");
  const emailColor = useColorModeValue("gray.600", "whiteAlpha.600");
  const trackBg = useColorModeValue("gray.100", "whiteAlpha.200");
  const completeColor = useColorModeValue("brand.600", "brand.300");
  const muted = useColorModeValue("gray.500", "whiteAlpha.600");

  const fullName = [user?.firstName || t("settings.firstName"), user?.lastName || t("settings.lastName")]
    .filter(Boolean)
    .join(" ");
  const email = user?.email?.trim() || "";
  const clamped = Math.max(0, Math.min(100, Number(completionPercent) || 0));

  return (
    <Box px={4} py={4}>
      <Grid templateColumns={`${AVATAR_SIZE} 1fr`} columnGap={3} alignItems="center">
        <Box h={AVATAR_SIZE} display="flex" alignItems="center" justifyContent="center">
          <ProfileAvatarUploader
            variant="compact"
            showEditBadge
            userId={userId}
            user={user}
            avatarUrl={avatarUrl}
            onAvatarUpdated={onAvatarUpdated}
          />
        </Box>
        <Box display="flex" flexDirection="column" justifyContent="flex-start" minW={0}>
          <Flex align="center" gap={1} mt="4px" minW={0}>
            <Text
              as="h2"
              fontSize="sm"
              fontWeight="semibold"
              color={titleColor}
              lineHeight="1.2"
              wordBreak="break-word"
            >
              {fullName}
            </Text>
            <PremiumVerifiedBadge user={user} size={16} />
          </Flex>
          {email ? (
            <Text fontSize="xs" color={emailColor} mt={0} lineHeight="1.2" wordBreak="break-all">
              {email}
            </Text>
          ) : null}
        </Box>
      </Grid>

      <Box mt={3}>
        <Flex align="center" justify="space-between" gap={2} mb={1.5}>
          <Text fontSize="xs" fontWeight="medium" color={isProfileFullyComplete ? completeColor : muted}>
            {isProfileFullyComplete
              ? t("settings.profileFullyComplete")
              : t("settings.profileCompletion")}
          </Text>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color={isProfileFullyComplete ? completeColor : titleColor}
            tabularNums
          >
            {t("settings.profileCompletionPercent", { percent: clamped })}
          </Text>
        </Flex>
        <Progress
          value={clamped}
          size="sm"
          borderRadius="full"
          bg={trackBg}
          colorScheme="brand"
          sx={{
            "& > div": {
              transition: "width 0.25s ease",
            },
          }}
          aria-label={t("settings.profileCompletion")}
        />
      </Box>
    </Box>
  );
}
